import { FC } from "react";

import { OwnerType } from "@/domain/_shared/lib/owner-types";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQueryClient } from "@tanstack/react-query";

import { FileStorageUploader } from "../components/file-storage-uploader";
import { fileQueryDocument } from "../hooks/api/file.gql";
import { useCreateFile } from "../hooks/api/use-create-file";
import { useReplaceFile } from "../hooks/api/use-replace-file";

export interface UploadResult {
  file: File;
  url: string;
  error?: Error | undefined;
}

export interface FileUploadAction {
  id: string;
  newFile: File;
  oldFileId?: string | undefined;
  conflictHandling: "REPLACE" | "CREATE";
}

export interface FileItemUploaderProps {
  ownerId: string;
  ownerType: OwnerType;
  fileUploadAction: FileUploadAction;
  onFileUploadFinished?: (result: UploadResult) => void;
}

const SCAN_POLL_INTERVAL_MS = 2000;
const SCAN_POLL_MAX_ATTEMPTS = 60;

async function waitForScanCompletion(fileId: string): Promise<string> {
  const client = createOperationClient("file");

  for (let attempt = 0; attempt < SCAN_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, SCAN_POLL_INTERVAL_MS));

    try {
      const data = await client.request(fileQueryDocument, { input: { id: fileId } });

      if (!data?.file) {
        return "INFECTED";
      }

      if (data.file.scanStatus !== "PENDING") {
        return data.file.scanStatus;
      }
    } catch {
      return "ERROR";
    }
  }

  return "ERROR";
}

export const FileItemUploader: FC<FileItemUploaderProps> = ({
  fileUploadAction,
  ownerId,
  ownerType,
  onFileUploadFinished,
}) => {
  const queryClient = useQueryClient();
  const { createFile } = useCreateFile({ ownerId, ownerType });
  const { replaceFile } = useReplaceFile({ id: fileUploadAction.oldFileId ?? "", ownerId, ownerType });

  async function uploadWithoutConflict(result: UploadResult): Promise<string> {
    const data = await createFile({
      ownerId,
      ownerType,
      name: result.file.name,
      url: result.url,
      size: result.file.size,
    });
    return data.createFile.id;
  }

  async function uploadWithReplace(result: UploadResult, oldFileId: string): Promise<string> {
    const data = await replaceFile({
      id: oldFileId,
      url: result.url,
      ownerId,
      ownerType,
    });
    return data.replaceFile.id;
  }

  async function uploadWithCreate(result: UploadResult): Promise<string> {
    let filename = result.file.name;

    if (fileUploadAction.oldFileId) {
      const filenameWithoutExtension = filename.replace(/\.[^/.]+$/, "");
      const newFilenameWithoutExtension = `${filenameWithoutExtension} (Kopie)`;

      if (filenameWithoutExtension.length === filename.length) {
        filename = newFilenameWithoutExtension;
      } else {
        const fileExtension = filename.split(".").pop();
        filename = `${newFilenameWithoutExtension}.${fileExtension}`;
      }
    }

    const data = await createFile({
      ownerId,
      ownerType,
      name: filename,
      url: result.url,
      size: result.file.size,
    });
    return data.createFile.id;
  }

  async function handleFileUploadFinished(result: UploadResult) {
    if (result.error) {
      onFileUploadFinished?.(result);
      return;
    }

    try {
      let fileId: string;

      if (!fileUploadAction.oldFileId) {
        fileId = await uploadWithoutConflict(result);
      } else if (fileUploadAction.conflictHandling === "REPLACE") {
        fileId = await uploadWithReplace(result, fileUploadAction.oldFileId);
      } else {
        fileId = await uploadWithCreate(result);
      }

      const scanStatus = await waitForScanCompletion(fileId);

      queryClient.invalidateQueries({
        queryKey: queryKeys.files.list(ownerId, ownerType),
      });

      if (scanStatus === "CLEAN") {
        onFileUploadFinished?.(result);
        return;
      }

      const isMalware = scanStatus === "INFECTED";
      const error = Object.assign(
        new Error(isMalware ? "MALWARE_DETECTED" : `Scan failed with status: ${scanStatus}`),
        { isMalware },
      );
      throw error;
    } catch (e) {
      const cause = e instanceof Error ? e : new Error("Unknown persistence error", { cause: e });
      const isMalware = "isMalware" in cause && cause.isMalware === true;
      const error = Object.assign(
        new Error(
          cause.message
            ? `Could not save file "${result.file.name}": ${cause.message}`
            : `Could not save file "${result.file.name}". Please try again.`,
          { cause },
        ),
        { isMalware },
      );
      onFileUploadFinished?.({ ...result, error });
      throw error;
    }
  }

  return (
    <FileStorageUploader
      key={fileUploadAction.id}
      file={fileUploadAction.newFile}
      ownerId={ownerId}
      ownerType={ownerType}
      onFileUploadFinished={handleFileUploadFinished}
    />
  );
};
